// Documentations
// https://developer.zendesk.com/documentation/classic-web-widget-sdks/web-widget/quickstart-tutorials/web-widget-javascript-apis/

export interface Zendesk {
	(method: string, action: string, configOrCallback: unknown): void;
	readonly activate: VoidFunction;
	readonly hide: VoidFunction;
}

function isZendeskInstance(instance: unknown): instance is Zendesk {
	return typeof instance === 'function' && 'activate' in instance && 'hide' in instance;
}

export const TicketForms = {
	ContactUs: 5960325857565,
	Feedback: 6069013709597,
} as const satisfies Record<string, number>;

type TicketFormId = (typeof TicketForms)[keyof typeof TicketForms];

export enum Positions {
	Right = 'right',
	Bottom = 'bottom',
}

interface CustomField {
	readonly id: string;
	readonly defaultValue?: string;
}
export interface ZendeskSettings {
	readonly offsetX: number;
	readonly offsetY: number;
	readonly horizontalPosition: Positions;
	readonly verticalPosition: Positions;
	readonly formTitle: string;
	readonly attachments: boolean;
	readonly ticketForms: ReadonlyArray<TicketFormId>;
	readonly customFields: ReadonlyArray<CustomField>;
}

interface ZendeskModelProps {
	readonly zendeskInstance: Zendesk;
}

const ZENDESK_EVENTS = {
	/* cSpell:disable-next-line */
	Close: 'Web Widget Minimised',
	Open: 'Web Widget Opened',
} as const;

interface IZendeskModel {
	readonly zendeskInstance: Zendesk;

	readonly initialize: (settings: ZendeskSettings, disabledFields: ReadonlyArray<string>) => void;
	readonly updateSettings: (settings: ZendeskSettings) => void; // TODO - Do I really have to chain with ZendeskPrefill?
	readonly prefillFields: (disabledFields: ReadonlyArray<string>) => void;

	readonly onClose: (callback: VoidFunction) => void;
	readonly open: VoidFunction;
	readonly close: VoidFunction;
}
export class ZendeskModel implements IZendeskModel {
	readonly zendeskInstance: Zendesk;

	constructor({ zendeskInstance }: ZendeskModelProps) {
		if (!zendeskInstance) {
			throw new Error('Zendesk instance is required');
		}

		this.zendeskInstance = zendeskInstance;
	}

	static readonly create = (props: ZendeskModelProps): ZendeskModel => new ZendeskModel(props);

	readonly updateSettings = (settings: ZendeskSettings): void => {
		const {
			horizontalPosition,
			verticalPosition,
			offsetX,
			offsetY,
			formTitle,
			attachments,
			customFields,
			ticketForms,
		} = settings;

		this.zendeskInstance('webWidget', 'updateSettings', {
			webWidget: {
				position: {
					horizontal: horizontalPosition,
					vertical: verticalPosition,
				},
				offset: {
					horizontal: `${offsetX}px`,
					vertical: `${offsetY}px`,
				},
				contactForm: {
					title: {
						'*': formTitle,
					},
					attachments,
					fields: customFields.map(function getField(prefill) {
						const { id, defaultValue } = prefill;
						return {
							id,
							...(defaultValue && { prefill: { '*': defaultValue } }),
						};
					}),
					ticketForms: ticketForms.map(function getTicketForm(ticketForm) {
						return { id: ticketForm };
					}),
				},
			} as const,
		});
	};

	readonly prefillFields = (disabledFields: ReadonlyArray<string>): void => {
		const readOnlyFields = disabledFields.reduce<Record<string, { readonly readOnly: boolean }>>((acc, field) => {
			acc[field] = { readOnly: true } as const;
			return acc;
		}, {});

		this.zendeskInstance('webWidget', 'prefill', readOnlyFields);
	};

	readonly initialize = (settings: ZendeskSettings, disabledFields: ReadonlyArray<string>): void => {
		this.updateSettings(settings);
		this.prefillFields(disabledFields);
	};

	readonly onClose = (callback: VoidFunction): void => this.onUserEventById(ZENDESK_EVENTS.Close, callback);

	readonly onOpen = (callback: VoidFunction): void => this.onUserEventById(ZENDESK_EVENTS.Open, callback);

	readonly onUserEventById = (eventId: string, callback: VoidFunction): void => {
		this.zendeskInstance('webWidget:on', 'userEvent', (userEvent: { readonly action: string }): void => {
			if (userEvent.action === eventId) {
				callback();
			}
		});
	};

	readonly onUserEvent = (callback: VoidFunction): void => {
		this.zendeskInstance('webWidget:on', 'userEvent', callback);
	};

	readonly open = (): void => {
		this.zendeskInstance.activate();
	};

	readonly close = (): void => {
		this.zendeskInstance.hide();
	};
}

const TIMEOUT = 15_000;
const ZENDESK_VARIABLE_NAME = 'zE';
let zendeskPromise: Promise<Zendesk> | null = null;

export async function getZendesk(): Promise<Zendesk> {
	if (zendeskPromise !== null) {
		return zendeskPromise;
	}

	zendeskPromise = Promise.race([
		new Promise<Zendesk>((resolve) => {
			awaitZendeskInstance().then((instance) => {
				resolve(instance);
			});
		}),
		new Promise<Zendesk>(function setTimeoutError(_, reject) {
			setTimeout(() => reject(new Error('Zendesk instance not found (timeout)')), TIMEOUT);
		}),
	]);

	return zendeskPromise;
}

function awaitZendeskInstance(): Promise<Zendesk> {
	return new Promise<Zendesk>((resolve) => {
		if (ZENDESK_VARIABLE_NAME in window && isZendeskInstance(window[ZENDESK_VARIABLE_NAME])) {
			resolve(window[ZENDESK_VARIABLE_NAME]);
		} else {
			let instance: Zendesk | undefined;
			Object.defineProperty(window, ZENDESK_VARIABLE_NAME, {
				configurable: true,
				get() {
					return instance;
				},
				set(value) {
					instance = value;
					if (isZendeskInstance(value)) {
						value.hide();
						resolve(value);
					}
				},
			});
		}
	});
}

let zendeskScriptElement: HTMLScriptElement | null = null;
export function embedZendeskScriptOnce(): void {
	zendeskScriptElement ??= createZendeskScriptElement();

	if (!document.body.contains(zendeskScriptElement)) {
		embedZendeskScript(zendeskScriptElement);
	}
}
function createZendeskScriptElement(): HTMLScriptElement {
	const script = document.createElement('script');

	script.id = 'ze-snippet';
	script.src = 'https://static.zdassets.com/ekr/snippet.js?key=06156e85-f1b7-49e4-bba3-d902643f8234';
	script.async = true;

	return script;
}

function embedZendeskScript(script: HTMLScriptElement): void {
	document.body.appendChild(script);
}
